import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { debugLogger } from '@/utils/debugLogger';

interface BoostedProfile {
  user_id: string;
  username: string;
  main_photo_url: string | null;
  activated_at: string;
  expires_at: string;
}

export const useActiveBoostedProfiles = () => {
  const { user } = useAuth();
  const [boostedProfiles, setBoostedProfiles] = useState<BoostedProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasActiveBoost, setHasActiveBoost] = useState(false);

  const fetchBoostedProfiles = useCallback(async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .rpc('get_active_boosted_profiles');

      if (error) {
        debugLogger.error('Boosts', 'Error fetching boosted profiles:', error);
        return;
      }

      setBoostedProfiles(data || []);
      
      // Check if current user has active boost
      if (user?.id && data) {
        setHasActiveBoost(data.some((p: BoostedProfile) => p.user_id === user.id));
      }

      debugLogger.debug('Boosts', 'Fetched boosted profiles:', { count: data?.length || 0 });
    } catch (error) {
      debugLogger.error('Boosts', 'Error in fetchBoostedProfiles:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchBoostedProfiles();
  }, [fetchBoostedProfiles]);

  return {
    boostedProfiles,
    loading,
    hasActiveBoost,
    refresh: fetchBoostedProfiles
  };
};
